import { useMemo } from 'react';
import { Card, Badge, Toggle, MultiSelect, InterruptConfig, TagPicker } from '../shared';

function getToolMcpId(toolName) {
    if (!toolName.includes('__')) return null;
    const prefix = toolName.split('__')[0];
    return prefix.replace(/_/g, '-');
}

const capabilityOn = (dict) => dict != null && dict.enabled === true;
const toggleCapability = (dict, enabled) => ({ ...(dict || {}), enabled });
const patchCapability = (dict, patch) => ({ ...(dict || {}), ...patch });

export default function TabToolsSafety({
    formData, handleChange,
    availableTools, toolkits, availableMCPServers,
    interruptDefaults, mcpServerDetails, loadingOptions,
    agentDefaults = {},
}) {
    const capabilityDefaults = agentDefaults.capabilities || {};
    const excludedMcpIds = useMemo(() => {
        if (formData.mcp_enabled === false) return null;
        return new Set(formData.mcp_exclude || []);
    }, [formData.mcp_enabled, formData.mcp_exclude]);

    const includedMcpIds = useMemo(() => {
        if (formData.mcp_enabled === false) return new Set();
        return formData.mcp_include && formData.mcp_include.length > 0
            ? new Set(formData.mcp_include)
            : null;
    }, [formData.mcp_enabled, formData.mcp_include]);

    const filteredTools = useMemo(() => {
        if (formData.mcp_enabled === false) {
            return availableTools.filter(t => !t.is_mcp);
        }
        return availableTools.filter(t => {
            if (!t.is_mcp) return true;
            const mcpId = getToolMcpId(t.value);
            if (!mcpId) return true;
            if (excludedMcpIds && excludedMcpIds.has(mcpId)) return false;
            if (includedMcpIds) return includedMcpIds.has(mcpId);
            return true;
        });
    }, [availableTools, formData.mcp_enabled, excludedMcpIds, includedMcpIds]);

    const toolsCap = formData.tools || {};
    const toolsEnabled = toolsCap.enabled !== false;

    const effectiveTools = useMemo(() => {
        if (!toolsEnabled && formData.mcp_enabled === false) return [];
        const included = toolsCap.include;
        const excluded = new Set(toolsCap.exclude || []);
        const includeTags = new Set(toolsCap.include_tags || []);
        const excludeTags = new Set(toolsCap.exclude_tags || []);
        return filteredTools.filter(t => {
            if (excluded.has(t.value)) return false;
            if (included && included.length > 0 && !included.includes(t.value)) return false;
            const tags = t.tags || [];
            if (includeTags.size > 0 && !tags.some(tag => includeTags.has(tag))) return false;
            if (excludeTags.size > 0 && tags.some(tag => excludeTags.has(tag))) return false;
            return true;
        });
    }, [filteredTools, toolsCap.include, toolsCap.exclude, toolsCap.include_tags, toolsCap.exclude_tags, toolsEnabled, formData.mcp_enabled]);

    return (
        <div className="space-y-4">
            <Card title={<span className="flex items-center gap-2">1. MCP Servers <Toggle checked={formData.mcp_enabled !== false} onChange={(val) => handleChange('mcp_enabled', val)} size="sm" /></span>}>
                {formData.mcp_enabled !== false && (
                    <div className="grid grid-cols-2 gap-3">
                        <MultiSelect
                            label="Include"
                            options={availableMCPServers}
                            selected={formData.mcp_include || []}
                            onChange={(val) => handleChange('mcp_include', val.length > 0 ? val : null)}
                            placeholder="All servers..."
                            loading={loadingOptions}
                        />
                        <MultiSelect
                            label="Exclude"
                            options={availableMCPServers}
                            selected={formData.mcp_exclude || []}
                            onChange={(val) => handleChange('mcp_exclude', val.length > 0 ? val : null)}
                            placeholder="Exclude servers..."
                            loading={loadingOptions}
                        />
                    </div>
                )}
            </Card>

            <Card title={<span className="flex items-center gap-2">2. Tools <Toggle checked={toolsEnabled} onChange={(val) => handleChange('tools', toggleCapability(formData.tools, val))} size="sm" /></span>}>
                {toolsEnabled && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <TagPicker
                                label="Include Tags"
                                placeholder="All tags..."
                                tags={toolkits}
                                availableTools={availableTools}
                                selected={toolsCap.include_tags || []}
                                onChange={(val) => handleChange('tools', patchCapability(formData.tools, { include_tags: val }))}
                                modalTitle="Include Tags"
                                modalDescription="Only tools tagged with at least one selected tag will be available."
                            />
                            <TagPicker
                                label="Exclude Tags"
                                placeholder="Exclude tags..."
                                tags={toolkits}
                                availableTools={availableTools}
                                selected={toolsCap.exclude_tags || []}
                                onChange={(val) => handleChange('tools', patchCapability(formData.tools, { exclude_tags: val }))}
                                modalTitle="Exclude Tags"
                                modalDescription="Tools tagged with any selected tag will be blocked."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <MultiSelect
                                label="Include"
                                options={filteredTools}
                                selected={toolsCap.include || []}
                                onChange={(val) => handleChange('tools', patchCapability(formData.tools, { include: val }))}
                                placeholder="All tools..."
                                loading={loadingOptions}
                            />
                            <MultiSelect
                                label="Exclude"
                                options={filteredTools}
                                selected={toolsCap.exclude || []}
                                onChange={(val) => handleChange('tools', patchCapability(formData.tools, { exclude: val }))}
                                placeholder="Exclude tools..."
                                loading={loadingOptions}
                            />
                        </div>
                    </div>
                )}
                {effectiveTools.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-gray-500">Effective tools</span>
                            <Badge variant="outline">{effectiveTools.length}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                            {effectiveTools.map(t => (
                                <Badge key={t.value} variant="outline">{t.label}</Badge>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    3. Tool Search (Progressive Discovery)
                    <select
                        className="text-xs border rounded px-2 py-1"
                        value={
                            formData.tool_search?.enabled === true ? 'on'
                            : formData.tool_search?.enabled === false ? 'off'
                            : 'inherit'
                        }
                        onChange={(e) => {
                            const v = e.target.value;
                            const enabled = v === 'on' ? true : v === 'off' ? false : null;
                            handleChange('tool_search', { ...(formData.tool_search || {}), enabled });
                        }}
                    >
                        <option value="inherit">Inherit (env default)</option>
                        <option value="on">On</option>
                        <option value="off">Off</option>
                    </select>
                </span>
            }>
                <p className="text-xs text-gray-500 mb-3">
                    The model sees only always-include tools + <code className="px-1 py-0.5 bg-gray-100 rounded">tool_search</code> initially,
                    then calls <code className="px-1 py-0.5 bg-gray-100 rounded">tool_search</code> to discover more tools on demand.
                    <br/>
                    <strong>Inherit</strong>: follow env var <code className="px-1 bg-gray-100 rounded">TOOL_SEARCH_ENABLED</code>.{' '}
                    <strong>On/Off</strong>: override env for this agent.
                </p>
                {formData.tool_search?.enabled === true ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <TagPicker
                                label="Include Tags"
                                placeholder="All tags..."
                                tags={toolkits}
                                availableTools={availableTools}
                                selected={formData.tool_search?.include_tags || []}
                                onChange={(val) => handleChange('tool_search', patchCapability(formData.tool_search, { include_tags: val }))}
                                modalTitle="Always-Include Tags"
                                modalDescription="Tools tagged with any selected tag will always be visible to the model."
                            />
                            <TagPicker
                                label="Exclude Tags"
                                placeholder="Exclude tags..."
                                tags={toolkits}
                                availableTools={availableTools}
                                selected={formData.tool_search?.exclude_tags || []}
                                onChange={(val) => handleChange('tool_search', patchCapability(formData.tool_search, { exclude_tags: val }))}
                                modalTitle="Exclude Tags"
                                modalDescription="Tools tagged with any selected tag will be excluded from the always-visible set."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <MultiSelect
                                label="Include"
                                options={availableTools}
                                selected={formData.tool_search?.include ?? (capabilityDefaults.tool_search?.include || [])}
                                onChange={(val) =>
                                    handleChange('tool_search', patchCapability(formData.tool_search, { include: val }))
                                }
                                placeholder="Always-visible tools..."
                            />
                            <MultiSelect
                                label="Exclude"
                                options={availableTools}
                                selected={formData.tool_search?.exclude || []}
                                onChange={(val) =>
                                    handleChange('tool_search', patchCapability(formData.tool_search, { exclude: val }))
                                }
                                placeholder="Exclude tools..."
                            />
                        </div>
                        <p className="text-xs text-gray-500">
                            Tools resolved from <strong>Include</strong> + <strong>Include Tags</strong>, then filtered by
                            <strong> Exclude</strong> + <strong>Exclude Tags</strong> are visible to the model on every turn,
                            regardless of discovery state.
                            Framework tools (filesystem, task, send_file_to_user, write_todos, etc.) are auto-included separately.
                            {(capabilityDefaults.tool_search?.include?.length ?? 0) > 0 && (
                                <> Default include: <code className="px-1 bg-gray-100 rounded">{(capabilityDefaults.tool_search?.include || []).join(', ')}</code>.</>
                            )}
                        </p>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max results per query
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={20}
                                className="w-32 px-2 py-1 text-sm border rounded"
                                value={formData.tool_search?.max_results_per_query ?? 5}
                                onChange={(e) =>
                                    handleChange('tool_search', patchCapability(formData.tool_search, {
                                        max_results_per_query: parseInt(e.target.value, 10) || 5,
                                    }))
                                }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Maximum tools returned per query (default 5).
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-gray-500">
                        Tool search disabled. All tools loaded into context every turn (legacy behavior).
                    </p>
                )}
            </Card>

            <Card title={<span className="flex items-center gap-2">4. Summarization <Toggle checked={capabilityOn(formData.summarization)} onChange={(val) => handleChange('summarization', toggleCapability(formData.summarization, val))} size="sm" /></span>}>
                <p className="text-xs text-gray-500 mb-3">
                    Automatically compacts conversation when token usage exceeds a threshold. Older messages are summarized via LLM and offloaded to storage.
                    When disabled, no compaction occurs (conversation grows unbounded).
                </p>
                {capabilityOn(formData.summarization) && (() => {
                    const sd = capabilityDefaults.summarization || {};
                    const defTrigger = sd.trigger || [];
                    const defKeep = sd.keep || [];
                    const cur = formData.summarization || {};
                    const curTrigger = cur.trigger;
                    const curKeep = cur.keep;
                    return (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <SummarizationField
                                    label="Trigger"
                                    current={curTrigger}
                                    defaultValue={defTrigger}
                                    onChange={(val) => handleChange('summarization', patchCapability(cur, { trigger: val }))}
                                />
                                <SummarizationField
                                    label="Keep"
                                    current={curKeep}
                                    defaultValue={defKeep}
                                    onChange={(val) => handleChange('summarization', patchCapability(cur, { keep: val }))}
                                />
                            </div>
                        </div>
                    );
                })()}
            </Card>

            <Card title="5. HITL / Interrupt">
                <InterruptConfig
                    enabled={formData.hitl_enabled !== false}
                    mode={formData.interrupt_on ? 'custom' : 'default'}
                    interruptOn={formData.interrupt_on || interruptDefaults}
                    defaultInterruptOn={interruptDefaults}
                    availableTools={effectiveTools}
                    mcpServers={mcpServerDetails}
                    onEnabledChange={(val) => handleChange('hitl_enabled', val)}
                    onModeChange={(mode) => {
                        if (mode === 'default') handleChange('interrupt_on', null);
                        else handleChange('interrupt_on', { ...interruptDefaults });
                    }}
                    onInterruptOnChange={(val) => handleChange('interrupt_on', val)}
                />
            </Card>
        </div>
    );
}

function SummarizationField({ label, current, defaultValue, onChange }) {
    const defUnit = defaultValue?.[0] || 'tokens';
    const defAmount = defaultValue?.[1] ?? '';
    const curUnit = current?.[0] || defUnit;
    const curAmount = current?.[1] ?? defAmount;

    return (
        <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
            <div className="flex gap-1">
                <select
                    className="text-xs border rounded px-1"
                    value={curUnit}
                    onChange={(e) => onChange([e.target.value, current?.[1] ?? defAmount])}
                >
                    <option value="tokens">tokens</option>
                    <option value="messages">messages</option>
                    <option value="fraction">fraction</option>
                </select>
                <input
                    type="number"
                    step={curUnit === 'fraction' ? '0.01' : '1'}
                    className="w-28 px-2 py-1 text-sm border rounded"
                    placeholder={String(defAmount)}
                    value={curAmount}
                    onChange={(e) => {
                        const val = curUnit === 'fraction' ? parseFloat(e.target.value) : parseInt(e.target.value, 10);
                        onChange(val || val === 0 ? [curUnit, val] : null);
                    }}
                />
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Default: {defUnit} {defAmount}</p>
        </div>
    );
}
